import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { clientReviewService } from '../../../services/clientReviewService';
import { useAuth } from '../../../contexts/AuthContext';

const ReviewSubmissionModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [starRating, setStarRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [usagePeriodDays, setUsagePeriodDays] = useState('');
  const [complianceMetrics, setComplianceMetrics] = useState({
    nasfundAccuracy: '',
    swtEfficiency: '',
    employmentActScore: '',
    costSavingsKina: '',
    violationsPrevented: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!user) {
      setError('You must be logged in to submit a review');
      return;
    }

    if (starRating === 0) {
      setError('Please select a star rating');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const reviewData = {
        companyId,
        starRating,
        reviewText,
        systemUsagePeriodDays: parseInt(usagePeriodDays) || 0,
        complianceSuccessMetrics: {
          nasfund_accuracy_improvement: complianceMetrics?.nasfundAccuracy ? `${complianceMetrics?.nasfundAccuracy}%` : null,
          swt_processing_efficiency: complianceMetrics?.swtEfficiency ? `${complianceMetrics?.swtEfficiency}% faster` : null,
          employment_act_adherence_score: complianceMetrics?.employmentActScore ? `${complianceMetrics?.employmentActScore}%` : null,
          cost_savings_kina: complianceMetrics?.costSavingsKina ? parseFloat(complianceMetrics?.costSavingsKina) : null,
          compliance_violations_prevented: complianceMetrics?.violationsPrevented ? parseInt(complianceMetrics?.violationsPrevented) : null
        }
      };

      await clientReviewService?.submitReview(reviewData);
      
      // Reset form
      setStarRating(0);
      setReviewText('');
      setCompanyId('');
      setUsagePeriodDays('');
      setComplianceMetrics({
        nasfundAccuracy: '',
        swtEfficiency: '',
        employmentActScore: '',
        costSavingsKina: '',
        violationsPrevented: ''
      });

      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMetricChange = (field, value) => {
    setComplianceMetrics(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="MessageSquare" size={24} className="text-emerald-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Submit Your Review</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Overall Rating *
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5]?.map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setStarRating(rating)}
                  onMouseEnter={() => setHoverRating(rating)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Icon
                    name="Star"
                    size={32}
                    className={(
                      rating <= (hoverRating || starRating)
                        ? 'text-yellow-500 fill-yellow-500' :'text-gray-300 dark:text-gray-600'
                    )}
                  />
                </button>
              ))}
              {starRating > 0 && (
                <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {starRating}/5
                </span>
              )}
            </div>
          </div>

          {/* Review Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your Review
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e?.target?.value)}
              rows={4}
              placeholder="Share your experience with our HR compliance platform..."
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* System Usage Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              System Usage Period (Days)
            </label>
            <input
              type="number"
              value={usagePeriodDays}
              onChange={(e) => setUsagePeriodDays(e?.target?.value)}
              placeholder="e.g., 90"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Compliance Success Metrics */}
          <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="CheckCircle2" size={18} className="text-emerald-600" />
              Compliance Success Metrics (Optional)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  NASFUND Accuracy Improvement (%)
                </label>
                <input
                  type="number"
                  value={complianceMetrics?.nasfundAccuracy}
                  onChange={(e) => handleMetricChange('nasfundAccuracy', e?.target?.value)}
                  placeholder="e.g., 95"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  SWT Processing Efficiency (%)
                </label>
                <input
                  type="number"
                  value={complianceMetrics?.swtEfficiency}
                  onChange={(e) => handleMetricChange('swtEfficiency', e?.target?.value)}
                  placeholder="e.g., 80"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Employment Act Adherence Score (%)
                </label>
                <input
                  type="number"
                  value={complianceMetrics?.employmentActScore}
                  onChange={(e) => handleMetricChange('employmentActScore', e?.target?.value)}
                  placeholder="e.g., 98"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Cost Savings (PNG Kina)
                </label>
                <input
                  type="number"
                  value={complianceMetrics?.costSavingsKina}
                  onChange={(e) => handleMetricChange('costSavingsKina', e?.target?.value)}
                  placeholder="e.g., 150000"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Compliance Violations Prevented
                </label>
                <input
                  type="number"
                  value={complianceMetrics?.violationsPrevented}
                  onChange={(e) => handleMetricChange('violationsPrevented', e?.target?.value)}
                  placeholder="e.g., 45"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Company ID (Hidden - will be auto-populated from user context) */}
          <input type="hidden" value={companyId} />

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || starRating === 0}
              iconName={submitting ? 'Loader2' : 'Send'}
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewSubmissionModal;